import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { appCopy } from '@zibanu/shared';
import { Banner, Button, CodeInput } from '@zibanu/ui';
import { forgetAnotherPerson } from '../app/device';
import { useDeps } from '../app/deps';
import { UpgradeNotice } from '../app/Gate';
import { loginFlow } from '../app/loginFlow';

const TRIES = 5;
const RESEND_AFTER_S = 60;

/** "Enter the code": six digits, tries counted on the device, a new code after a minute (spec 3A §7.9). */
export function LoginCode() {
  const deps = useDeps();
  const { client, platform } = deps;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const phone = loginFlow.get();
  const [code, setCode] = useState('');
  const [wrong, setWrong] = useState(0);
  const [message, setMessage] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(RESEND_AFTER_S);
  // The right code, but the app isn't part of their plan: no session (phase 1 D1).
  const [noApp, setNoApp] = useState(false);

  useEffect(() => {
    if (!phone) void navigate({ to: '/login', replace: true });
  }, [phone, navigate]);
  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  if (!phone) return null;
  if (noApp) {
    return (
      <main className="zw-stop">
        <UpgradeNotice />
      </main>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await client.auth.verifyCode(phone, code, platform.deviceLabel());
      if (res.ok) {
        await forgetAnotherPerson(deps, res.data.person.personId);
        loginFlow.set(null);
        queryClient.setQueryData(['me'], res.data);
        await navigate({ href: loginFlow.takePlace(), replace: true });
        return;
      }
      if (res.error === 'invalid_input') {
        const left = TRIES - (wrong + 1);
        setWrong(wrong + 1);
        setMessage(left > 0 ? appCopy.codeWrong(left) : appCopy.codeSpent());
      } else if (res.error === 'forbidden' && res.reason === 'no_app') {
        setNoApp(true);
      } else if (res.error === 'forbidden') {
        setMessage(res.reason === 'dormant' ? appCopy.codeDormant() : appCopy.codeLocked());
      } else if (res.error === 'rate_limited') {
        setMessage(appCopy.loginBusy());
      } else {
        setMessage(appCopy.ourError());
      }
    } catch {
      setMessage(appCopy.ourError());
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      const res = await client.auth.requestCode(phone);
      if (!res.ok) {
        setMessage(res.error === 'rate_limited' ? appCopy.loginBusy() : appCopy.ourError());
        return;
      }
      setWrong(0);
      setMessage(undefined);
      setCode('');
      setWait(RESEND_AFTER_S);
    } catch {
      setMessage(appCopy.ourError());
    }
  };

  return (
    <main className="zw-login">
      <div className="zw-word">{appCopy.wordmark()}</div>
      <h1 className="zw-h1">{appCopy.codeTitle()}</h1>
      <p className="zw-lead">{appCopy.codeLead(phone.slice(-4))}</p>
      <form className="zw-login__form" onSubmit={(e) => void submit(e)} noValidate>
        <CodeInput label={appCopy.codeLabel()} value={code} onChange={(e) => setCode(e.target.value)} />
        {message && <Banner tone="danger" title={message} />}
        <div className="zw-push">
          <Button type="submit" size="lg" block disabled={busy || wrong >= TRIES}>
            {appCopy.codeOpen()}
          </Button>
          <Button variant="ghost" disabled={wait > 0} onClick={() => void resend()}>
            {wait > 0 ? appCopy.codeResendIn(wait) : appCopy.codeResend()}
          </Button>
          {/* Back to the number, still filled in: an installed app has no back button. */}
          <Button variant="ghost" onClick={() => void navigate({ to: '/login', replace: true })}>
            {appCopy.codeChangeNumber()}
          </Button>
        </div>
      </form>
    </main>
  );
}
